import {useOverlayActivity} from "components/useOverlayActivity";
import {useState} from "react";
import {useMessages} from "i18n";
import {PolicyDialog, PolicyKind} from "legal/PolicyDialog";
import {AgreeButton, BrandTile, Copy, Header, PolicyLink, Root, Title} from "./Consent.styles";

/**
 * First-send consent gate (unified in ui#98), and since ui#1170 part of the drop area itself.
 *
 * It used to be a full-screen scrim {@link ../App} raised over the whole product the first time a
 * sender pressed Send. ui#1170 moved it INSIDE the create card, ui#1549 stopped it replacing the
 * sender panel, and ui#1575 took it out of the card's flow — so the drop area's own content sits
 * where it sits whether the gate is up or not. ui#1713 centred it over that content, ui#1728 made it
 * fill the card, ui#1752 put the heading beside the mark and coloured its italic accent, and ui#1757
 * moved the notice up under that row and set it at 16px. That is placement and looks only, in all seven
 * tickets: what acceptance means, how it is persisted and which documents the links open are unchanged,
 * and the notice sentence is unchanged to the byte. So is the TITLE — ui#1752 restyled the `em` the
 * catalog already emitted and added no copy of its own.
 *
 * <b>Why it covers the drop hero completely, and why that is the point rather than the ui#1549
 * defect returning.</b> Between ui#1575 and ui#1713 the gate was a 60px bar squeezed into the ~78px
 * the card has below the drop hero, because covering the upload control was the ui#1549 defect — a
 * paid click landing where the control measured 0x0. Two things retired that constraint. ui#1619
 * removed the capability pitch the card used to make, so there is nothing behind the gate left to
 * read; and the owner asked on 2026-08-30 for the heading back "in the nice font" and the panel "on
 * top of the drop files area on the center", then approved the ui#1728 takeover that finishes the
 * move. The distinction ui#1549 actually draws is between REMOVING the control and COVERING it, and
 * it still holds on the covering side: the upload control keeps its real box, at its real size,
 * does not MOVE between the two states, and comes back operable the moment the gate is answered
 * (`e2e/home-hero-editorial.spec.ts` checks that it stays visible and comes back operable). An opaque layer over it satisfies all four; a `display: none`
 * would fail every one, which is why the gate paints over the hero and never hides it.
 *
 * The heading is therefore rendered rather than visually hidden. It is the same `<h2>` under the same
 * `id`, so `aria-labelledby` names the region exactly as before — that wiring never depended on the
 * heading being visible — and a screen-reader user keeps both routes to the gate, landmark and
 * heading, that ui#1577's review round asked for. Since ui#1728 the words come from the rich-text
 * catalog so one of them can carry the serif italic accent; the text they render is still exactly
 * `consent.title`, so the name that wiring produces did not move either.
 *
 * The three document links open the CURRENT legal set through {@link PolicyDialog} — the same
 * dialog/documents the NavBar "Resources" menu and the cookie banner use — so what users agree to
 * here is exactly what the site publishes. Their LABELS are the gate's own `consent.links`, not the
 * menu's (ui#1585): Romanian needs the definite article here and must not have it in the menu.
 * ui#129: the "I agree" click covers the Terms; the Privacy Policy (the GDPR notice) and the Cookie
 * Policy are linked as acknowledged notices, and cookie consent itself stays with the banner's own
 * opt-in. The sentence carrying all three is `consentNotice` in the rich-text catalog, because the
 * links sit INSIDE it — fragments glued around link elements would freeze the English word order and
 * leave the component inventing the punctuation between the two sentences.
 *
 * A dumb view (epic #26): the acceptance record is written by {@link ./useTermsGate}, which owns the
 * gate state, so this component never touches storage.
 */

const TITLE_ID = "consent-gate-title";

interface Props {
	/**
	 * True while a destination CardSheet covers the drop area without unmounting it (ui#1061). The
	 * gate keeps its geometry behind the sheet — the covered canvas is deliberately unchanged — but
	 * leaves the keyboard/accessibility graph, exactly as {@link ../home/components/TransferPanel}'s
	 * controls do. Without this, a sender who has not accepted could tab into four buttons they cannot
	 * see and open a policy dialog from behind the sheet.
	 */
	covered: boolean;
	/** Click-through: record the acceptance and hand the sender panel back to the visitor. */
	onAgree: () => void;
}

export default function Consent({covered, onAgree}: Props) {
	const {consent, consentTitle, consentNotice} = useMessages();
	useOverlayActivity(!covered, undefined, false, false);
	// Which policy document is open, if any (same pattern as the NavBar Resources menu).
	const [policy, setPolicy] = useState<PolicyKind | null>(null);
	const policyLink = (kind: PolicyKind, label: string) => (
		<PolicyLink type="button" onClick={() => setPolicy(kind)}>{label}</PolicyLink>
	);
	return (
		<>
			<Root aria-labelledby={TITLE_ID} inert={covered}>
				{/* One row since ui#1752, where the two were stacked. A plain grouping box: it carries no
				  * role and no name, so the accessibility tree is the same tile-then-heading order it
				  * was, and only the visual arrangement moved. */}
				<Header>
					{/* Decorative: this region is already named by the heading beside it, and a screen
					  * reader announcing a bare "T" ahead of it would be noise rather than branding. */}
					<BrandTile aria-hidden="true"/>
					{/* The catalog renders the heading WITH its italic accent, and its text content is
					  * still exactly `consent.title` — which is what `aria-labelledby` resolves to and
					  * what names this region. `i18n.test.ts` holds the two to the same words in both
					  * locales. */}
					<Title id={TITLE_ID}>{consentTitle()}</Title>
				</Header>
				<Copy>
					{consentNotice(
						policyLink("terms", consent.links.terms),
						policyLink("privacy", consent.links.privacy),
						policyLink("cookie", consent.links.cookie),
					)}
				</Copy>
				<AgreeButton fullWidth onClick={onAgree}>{consent.agree}</AgreeButton>
			</Root>
			{/* A SIBLING of the gate, never inside it — the same arrangement the sender panel uses for
			  * its own dialogs. A destination sheet makes the gate `inert`, and a modal nested inside an
			  * inert subtree is one the reader can neither operate nor close. */}
			{policy !== null &&
			<PolicyDialog kind={policy} open onClose={() => setPolicy(null)}/>}
		</>
	);
};
